import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, 
  FileSearch, 
  BarChart3, 
  Mail, 
  CheckCircle, 
  ArrowRight,
  Upload,
  FileText,
  Download
} from 'lucide-react';

const Landing = () => {
  const features = [
    {
      icon: FileSearch,
      title: 'Multi-Source Detection',
      description: 'Check against web sources, academic journals (Semantic Scholar, CrossRef, OpenAlex), and internal repository.'
    },
    {
      icon: Shield,
      title: 'AI Content Detection',
      description: 'Advanced AI analysis detects ChatGPT and other AI-generated content patterns using GPT technology.'
    },
    {
      icon: BarChart3,
      title: 'Detailed Reports',
      description: 'Receive professional reports with source attribution, matched text excerpts, and similarity breakdown.'
    },
    {
      icon: CheckCircle,
      title: 'Student-to-Student Check',
      description: 'Internal repository comparison detects copying between student submissions.'
    }
  ];

  const steps = [
    { icon: Upload, title: 'Upload Document', description: 'Submit your PDF, DOCX, or TXT file' },
    { icon: FileSearch, title: 'Analysis', description: 'Our system scans against millions of sources' },
    { icon: Download, title: 'Get Report', description: 'Download or receive your report via email' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/images/rba-icon.png" 
                alt="Riverbank Academy Logo" 
                className="h-12 w-auto"
              />
              <div className="hidden sm:block">
                <h1 className="font-serif text-lg font-semibold text-foreground leading-tight">Riverbank Academy</h1>
                <p className="text-xs text-muted-foreground">Integrity Checker - "Empowering Life"</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="login-nav-btn">Login</Button>
              </Link>
              <Link to="/register">
                <Button data-testid="register-nav-btn">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src="/images/rba-logo.png" 
                  alt="Riverbank Academy Logo" 
                  className="h-20 w-auto"
                />
                <div>
                  <span className="inline-block px-3 py-1 bg-accent/20 text-accent-foreground text-sm font-medium rounded-sm">
                    Academic Integrity Solution
                  </span>
                </div>
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tighter leading-tight mb-6">
                Riverbank <span className="text-accent">Academy</span>
              </h1>
              <h2 className="font-serif text-2xl text-muted-foreground mb-4">
                Plagiarism & AI Content Detection System
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
                Students can submit their academic papers for comprehensive 
                plagiarism checking and AI content detection before final submission.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="shadow-md" data-testid="hero-get-started-btn">
                    Get Started Free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" size="lg" data-testid="hero-login-btn">
                    Student Login
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center gap-8 mt-12 pt-8 border-t border-border">
                <div>
                  <p className="font-mono text-3xl font-bold text-foreground">99%</p>
                  <p className="text-sm text-muted-foreground">Detection Accuracy</p>
                </div>
                <div>
                  <p className="font-mono text-3xl font-bold text-foreground">100MB</p>
                  <p className="text-sm text-muted-foreground">Max File Size</p>
                </div>
                <div>
                  <p className="font-mono text-3xl font-bold text-foreground">5+</p>
                  <p className="text-sm text-muted-foreground">File Formats</p>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:block animate-fade-in">
              <div className="relative">
                <div 
                  className="rounded-sm overflow-hidden shadow-2xl"
                  style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1685456891912-c09f9cd252eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYXJjaGl0ZWN0dXJlJTIwbW9kZXJufGVufDB8fHx8MTc2NjEyODQ1N3ww&ixlib=rb-4.1.0&q=85)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    height: '500px'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent rounded-sm" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMPORTANT NOTICE BANNER */}
      <section className="py-8 bg-amber-50 border-y-2 border-amber-300" data-testid="important-notice">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-amber-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-serif text-xl font-bold text-amber-900 mb-3">
                ⚠️ Important Notice & Exclusion Clause
              </h3>
              <div className="text-amber-800 space-y-3">
                <p className="font-semibold">This system performs the following checks on submitted documents:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Plagiarism Detection:</strong> Compares work against academic journals, web sources, and previous student submissions</li>
                  <li><strong>AI Content Detection:</strong> Identifies text potentially generated by ChatGPT, Claude, or other AI writing tools</li>
                  <li><strong>Internal Repository Check:</strong> Compares against previously submitted student papers within the university</li>
                </ul>
                
                <div className="mt-4 p-4 bg-white rounded-lg border-2 border-amber-400">
                  <h4 className="font-bold text-amber-900 mb-2 text-lg">EXCLUSION CLAUSE & DISCLAIMER</h4>
                  <div className="text-sm space-y-2 text-amber-900">
                    <p>
                      <strong>1. Interpretation of Results:</strong> A high similarity score or AI detection percentage does <strong>NOT</strong> constitute 
                      evidence of academic misconduct, plagiarism, or dishonesty. Such scores may result from properly cited quotations, 
                      common academic terminology, standard phrases, bibliographic references, or coincidental textual similarities.
                    </p>
                    <p>
                      <strong>2. Human Review Required:</strong> All results generated by this system are intended solely as preliminary 
                      indicators and must be subject to review and interpretation by qualified academic staff before any determination 
                      regarding academic integrity is made.
                    </p>
                    <p>
                      <strong>3. AI Detection Limitations:</strong> AI content detection technology analyzes writing patterns but cannot 
                      definitively confirm whether content was generated by artificial intelligence. False positives may occur, particularly 
                      with formal academic writing styles. Results should be treated as indicative, not conclusive.
                    </p>
                    <p>
                      <strong>4. No Liability:</strong> Riverbank Academy and the operators of this system shall not be 
                      held liable for any academic decisions, actions, or consequences arising from the use of this tool or interpretation 
                      of its results.
                    </p>
                    <p>
                      <strong>5. Data Usage:</strong> By submitting documents, users consent to their work being stored in the internal 
                      repository for future comparison purposes, in accordance with the university's data retention policy.
                    </p>
                  </div>
                </div>
                
                <p className="text-xs text-amber-700 mt-3 italic">
                  By using this service, you acknowledge that you have read, understood, and agree to these terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Comprehensive Plagiarism Detection
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our advanced system provides thorough analysis to ensure academic integrity
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="card-hover border-border/60">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detection Sources Section */}
      <section className="py-16 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-foreground mb-4">
              What We Check Against
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our system performs comprehensive analysis across multiple sources
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 bg-muted/30 rounded-sm border border-border">
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="w-4 h-4 text-blue-700" />
                </div>
                Internal Repository
              </h4>
              <p className="text-sm text-muted-foreground">
                Previous student submissions within Riverbank Academy to detect student-to-student copying
              </p>
            </div>
            
            <div className="p-6 bg-muted/30 rounded-sm border border-border">
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <FileSearch className="w-4 h-4 text-emerald-700" />
                </div>
                Web Sources
              </h4>
              <p className="text-sm text-muted-foreground">
                Wikipedia, online articles, blogs, and publicly accessible web content
              </p>
            </div>
            
            <div className="p-6 bg-muted/30 rounded-sm border border-border">
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 text-purple-700" />
                </div>
                Academic Journals
              </h4>
              <p className="text-sm text-muted-foreground">
                Semantic Scholar, CrossRef, OpenAlex - over 250 million academic papers
              </p>
            </div>
            
            <div className="p-6 bg-muted/30 rounded-sm border border-border">
              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                  <Shield className="w-4 h-4 text-amber-700" />
                </div>
                AI Detection
              </h4>
              <p className="text-sm text-muted-foreground">
                ChatGPT, Claude, and other AI-generated content pattern recognition
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-lg text-muted-foreground">
              Three simple steps to check your document
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="text-center">
                  <div className="relative inline-block mb-6">
                    <div className="w-20 h-20 bg-primary rounded-sm flex items-center justify-center mx-auto">
                      <Icon className="w-10 h-10 text-primary-foreground" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center font-mono font-bold text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground text-lg mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Terms Section */}
      <section className="py-16 bg-muted/30" data-testid="exclusion-disclaimer">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                    Terms of Use & Technical Information
                  </h3>
                  <p className="text-muted-foreground">
                    Additional information about system capabilities and limitations
                  </p>
                </div>
              </div>
              
              <div className="space-y-4 text-sm">
                <div className="p-4 bg-muted/50 rounded-sm border border-border">
                  <h4 className="font-semibold text-foreground mb-2">Detection Sources</h4>
                  <p className="text-muted-foreground">
                    This system checks submitted documents against: academic journals and papers from Semantic Scholar, 
                    CrossRef, and OpenAlex databases (250+ million publications); publicly accessible web content; 
                    and the internal repository of previously submitted student work at Riverbank Academy.
                  </p>
                </div>
                
                <div className="p-4 bg-muted/50 rounded-sm border border-border">
                  <h4 className="font-semibold text-foreground mb-2">Content Excluded from Matching</h4>
                  <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Properly formatted quotations with citations</li>
                    <li>Bibliography, references, and works cited sections</li>
                    <li>Commonly used academic phrases and terminology</li>
                    <li>Matches below the minimum word threshold (configurable by administrator)</li>
                    <li>Document metadata, headers, and footers</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-muted/50 rounded-sm border border-border">
                  <h4 className="font-semibold text-foreground mb-2">Supported File Formats</h4>
                  <p className="text-muted-foreground">
                    PDF (.pdf), Microsoft Word (.doc, .docx), OpenDocument (.odt), Rich Text Format (.rtf), 
                    and Plain Text (.txt). Maximum file size: 100MB. There is no word count limit.
                  </p>
                </div>
                
                <div className="p-4 bg-muted/50 rounded-sm border border-border">
                  <h4 className="font-semibold text-foreground mb-2">Processing Time</h4>
                  <p className="text-muted-foreground">
                    Document analysis typically completes within 1-3 minutes depending on document length and 
                    system load. Users will see real-time status updates during processing.
                  </p>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-xs text-muted-foreground text-center">
                  For technical support or questions about this system, contact the Riverbank Academy IT Department.<br/>
                  For questions about academic integrity policies, contact the Riverbank Academy Office of Academic Integrity.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
            Ready to Check Your Document?
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of Riverbank Academy students maintaining academic integrity. 
            Get started with your first plagiarism check today.
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="shadow-lg" data-testid="cta-get-started-btn">
              Create Free Account
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <img 
                src="/images/rba-icon.png" 
                alt="Riverbank Academy Logo" 
                className="h-12 w-auto bg-white p-1 rounded"
              />
              <div>
                <span className="font-serif font-semibold text-lg">Riverbank Academy</span>
                <p className="text-sm opacity-80">Office of Academic Integrity</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm opacity-80">
                Plagiarism & AI Content Detection System
              </p>
              <p className="text-xs opacity-60 mt-1">
                © 2026 Riverbank Academy. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
